import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCategories, getMaterials, getAdmins } from '../api';
import { useTelegram } from '../context/TelegramContext';

const CategoriesScreen = () => {
  const navigate = useNavigate();
  const { user } = useTelegram();
  const [categories, setCategories] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [categoriesData, materialsData, adminsData] = await Promise.all([
          getCategories(),
          getMaterials(),
          getAdmins()
        ]);
        setCategories(categoriesData);
        setMaterials(materialsData);
        setAdmins(adminsData);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error("Failed to fetch data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getMaterialsCount = (categoryId) => {
    return materials.filter(material => material.category === categoryId).length;
  };

  const getMaterialPlural = (count) => {
    const number = Math.abs(count);
    const lastDigit = number % 10;
    const lastTwoDigits = number % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
      return 'материалов';
    }
    if (lastDigit === 1) {
      return 'материал';
    }
    if ([2, 3, 4].includes(lastDigit)) {
      return 'материала';
    }
    return 'материалов';
  };

  const handleCategoryClick = (categoryId) => {
    navigate(`/category/${categoryId}`);
  };

  if (loading) {
    return <div className="p-4 text-center">Загрузка...</div>;
  }

  if (error) {
    return <div className="p-4 text-center text-red-500">Ошибка: {error}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-md mx-auto">
        {/* Заголовок */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Навигация клуба
          </h1>
          <p className="text-gray-600">
            Выберите категорию для просмотра материалов
          </p>
        </div>

        {/* Сетка категорий */}
        <div className="grid grid-cols-2 gap-4">
          {categories.map((category) => {
            const count = getMaterialsCount(category.id);
            return (
              <div
                key={category.id}
                onClick={() => handleCategoryClick(category.id)}
                className="card p-6 cursor-pointer hover:shadow-lg transition-shadow duration-200 group flex flex-col items-center text-center"
              >
                {category.imageUrl ? (
                  <img src={category.imageUrl} alt={category.name} className="w-16 h-16 rounded-full object-cover mb-4 group-hover:scale-110 transition-transform duration-200"/>
                ) : (
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center text-2xl group-hover:scale-110 transition-transform duration-200">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l-1.586-1.586a2 2 0 00-2.828 0L6 14m6-6l.01.01"></path></svg>
                  </div>
                )}
                <h3 className="font-semibold text-gray-900 mb-1">
                  {category.name}
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  {category.description}
                </p>
                <div className="text-xs text-gold-600 font-medium">
                  {count} {getMaterialPlural(count)}
                </div>
              </div>
            )
          })}
        </div>

        {/* Кнопка админки */}
        <div className="mt-8 text-center">
          {user && admins.includes(user.id) && (
            <button
              onClick={() => navigate('/admin')}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Админ-панель
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoriesScreen; 